/**
 * #1731 (F-15, phase 3) — the app-level PiP widget mount.
 *
 * Port of cpv3 `features/chat-brand/GlobalVoicePip.tsx`. Rendered ONCE in
 * `MainLayout` beside the companion widget, so the widget — and the call
 * behind it — survives every in-app navigation. Renders nothing while idle,
 * for a viewer who is not a parent, or with the capability off. Hook order
 * is unconditional: every hook runs on every render.
 *
 * Also owns the re-attach on return: an app open (or refresh) within the
 * window silently reconnects the stored live session. Bounded to 5 minutes —
 * distinct from `sessionResume`'s 24h drawer window, because silently
 * re-opening the mic is only unsurprising right after leaving. On TRF a
 * re-attach mints a fresh room on the same thread (the thread is the record).
 */
import { useEffect, useRef } from 'react';
import { PipWidget } from '@core/voice/pip';
import { ChatSurfaceProvider } from '@core/config';
import {
  clearActiveSession,
  persistActiveSession,
  readResumableSession,
} from '@core/voice/sessionResume';
import { useChatConfig } from '@chat-config';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanionNameState } from '@/hooks/useCompanionName';
import {
  getPipVoiceSession,
  retryPipVoice,
  startPipVoice,
  usePipVoiceSnapshot,
} from './pipVoiceStore';

export const REATTACH_WINDOW_MS = 5 * 60 * 1000;
/** The stored session's owner tag; only calliope rooms are re-attached here. */
export const PIP_AGENT_TYPE = 'calliope';

export function GlobalVoicePip() {
  const { user } = useAuth();
  // #1806: a cold load that re-attaches mounts this widget before the persona
  // preferences land. `name` is null for exactly that moment; the copy still
  // needs a string ("<name> is speaking"), so the config keeps the resolved
  // fallback and only the DISC withholds its letter, via `identityPending`.
  const { name, isLoading: identityPending } = useCompanionNameState();
  const companionName = name ?? undefined;
  // #2109 R3-B24: re-resolves on a language switch, so the PiP's own chrome
  // (states, mute, end) follows without a reload.
  const surfaceConfig = useChatConfig('companion', { agentName: companionName });
  const enabled = surfaceConfig.capabilities.voicePip;
  const eligible = user?.role === 'parent' || user?.role === 'admin';
  const pip = usePipVoiceSnapshot();

  const isLiveNow = pip.state === 'listening' || pip.state === 'speaking';

  // Stored-session bookkeeping: the session is backend-owned; the widget is
  // the view. Persist while live so a refresh can re-attach; clear on end.
  useEffect(() => {
    if (isLiveNow && pip.conversationId) {
      persistActiveSession({
        conversationId: pip.conversationId,
        mode: 'hybrid',
        agentType: PIP_AGENT_TYPE,
        timestamp: Date.now(),
      });
    } else if (pip.state === 'ended') {
      clearActiveSession();
    }
  }, [isLiveNow, pip.state, pip.conversationId]);

  // Re-attach on return, once per app load, recent calliope sessions only.
  const reattachTried = useRef(false);
  useEffect(() => {
    if (!enabled || !eligible || reattachTried.current) return;
    reattachTried.current = true;
    if (pip.state !== 'idle') return;
    const stored = readResumableSession();
    if (!stored || stored.agentType !== PIP_AGENT_TYPE) return;
    if (Date.now() - stored.timestamp > REATTACH_WINDOW_MS) return;
    startPipVoice({ roomName: stored.conversationId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per app load, by design
  }, [enabled, eligible]);

  if (!enabled || !eligible) return null;

  return (
    <ChatSurfaceProvider config={surfaceConfig}>
      <PipWidget
        snapshot={pip}
        identityPending={identityPending}
        onMuteToggle={() => getPipVoiceSession().setMuted(!pip.muted)}
        onEnd={() => void getPipVoiceSession().end()}
        onDismiss={() => getPipVoiceSession().reset()}
        onRetry={retryPipVoice}
      />
    </ChatSurfaceProvider>
  );
}
